<template>
  <div
    class="video-wrapper"
    :class="{hover: isHovering}"
    @mouseover="isHovering = true"
    @mouseout="isHovering = false"
  >
    <share-overlay
      v-if="displayShareOverlay"
      :url="share.url"
      aria-hidden="true"
      tabindex="-1"
      @close="showShare"
    >
      <template #share-overlay-content>
        <p>{{ share.label }}</p>
        <div layout="row">
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.linkedin.com/shareArticle?url=' + share.url"
          >
            <c-icon
              name="linkedin"
              size="xl"
            />
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.twitter.com/intent/tweet?url=' + share.url"
          >
            <c-icon
              name="twitter"
              size="xl"
            />
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.facebook.com/sharer/sharer.php?u=' + share.url"
          >
            <c-icon
              name="facebook"
              size="xl"
            />
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            :href="'mailto:?Subject=' + share.emailSubject + '&amp;body=' + share.url"
          >
            <c-icon
              name="email"
              size="xl"
            />
          </a>
        </div>
        <copy-to-clipboard :text="share.url">
          <template #click-to-copy>
            <a
              class="button share-download-link"
              :content-download-url="share.url"
              content-download-type="video"
            >{{ share.buttonLabel }}</a>
          </template>
          <template #result>
            <p>{{ share.buttonResultText }}</p>
          </template>
        </copy-to-clipboard>
      </template>
    </share-overlay>

    <div
      v-if="!disableShare && !displayShareOverlay && share && share.url.length"
      class="gradientback"
      aria-hidden="true"
      tabindex="-1"
    >
      <a
        class="button button--header vt-video-share-button"
        @click="showShare"
      >{{ share.label }}</a>
    </div>

    <div
      v-definesize="autoSize"
      class="vt-video"
    >
      <!-- Poster -->
      <div
        v-if="!autoplay"
        tabindex="-1"
        :class="['vt-video__poster lazyload', { 'vt-video__poster--active' : playing }]"
        :data-bgset="`${poster} [(max-width: 767px)] | ${poster}`"
      />
    </div>
    <!-- Video -->
    <div
      v-show="loading"
      class="nt-loading nt-loading--block"
    >
      <div class="lds-ring">
        <div /><div /><div /><div />
      </div>
    </div>
    <div
      v-show="!loading"
      :id="id"
      class="yku-player"
      @click="changeStatus"
    />
  </div>
</template>

<script>
import ShareOverlay from '@molecules/share-overlay/share-overlay.vue'
import emitter from '../../../emitter'

export default {
  name: 'VideoYouku',
  components: {
    ShareOverlay
  },
  props: {
    autoplay: {
      type: Boolean,
      default: false
    },
    poster: {
      type: String,
      default: null,
      required: true
    },
    data: {
      type: String,
      default: null,
      required: true
    },
    autoSize: {
      type: Boolean
    },
    share: {
      type: Object,
      defult: null
    },
    disableShare: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      id: `video-${Math.floor(Math.random() * 100000)}`,
      playing: false,
      player: null,
      loading: true,
      displayShareOverlay: false,
      isHovering: false
    }
  },
  watch: {
    playing(isPlaying) {
      if (isPlaying) {
        this.player.playVideo()
      } else {
        this.player.pauseVideo()
      }
    }
  },
  created() {
    window.addEventListener('onLoadYoukuPlayer', this.onLoadYoukuPlayer)

    emitter.$on('vt-change-video', (payload) => {
      if (payload.id === this.id) {
        switch (payload.command) {
          case 'pause':
            if (!this.playing) return

            this.playing = false
            break
          case 'stop':
            if (this.playing) {
              this.player.seekTo(0)
            }
            this.playing = false
            break
          default:
            window.console.warn(`Command ${payload.command} not found.`)
            break
        }
      }
    })
  },
  mounted() {
      this.player = new YKU.Player(this.id, { // eslint-disable-line
      styleid: '0',
      client_id: '44503cca1be605b5',
      vid: this.data,
      newPlayer: true,
      show_related: false,
      autoplay: this.autoplay,
      events: {
        onPlayerReady: this.onYoukuPlayerReady
      }
    })
  },
  methods: {
    changeStatus() {
      this.playing = !this.playing
    },
    showShare() {
      this.displayShareOverlay = !this.displayShareOverlay

      if (this.displayShareOverlay) {
        this.playing = false
      }
    },
    onLoadYoukuPlayer(data) {
      const el = document.getElementById(this.id)

      if (el) {
        el.style.width = '100%'
        el.style.height = '100%'
      }

      if (data.detail === this.id && this.autoplay) {
        this.playing = true
      }
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
  .vt-video {
    overflow: hidden;
    width: 100%;

    .yku-player {
      background: #000;
      border: 0;
      outline: none;
      position: absolute;
      left: 0;
      top: 0;
      z-index: 0;
    }

    &__poster {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: #f8f8f8;
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      z-index: 1;

      & button {
        z-index: 3;
      }

      &--active {
        visibility: hidden;
      }
    }
  }
</style>
